import { useState, useEffect, useCallback, useRef } from "react"
import { ArrowLeft, Edit, Plus, Trash2 } from "lucide-react"
import { useParams, useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from "@f2e/ui/components/base"
import {
  Brand,
  BrandOAuthProvider,
  BrandEditData,
  OAuthProviderEditData,
  Workspace,
  Permission,
} from "@/types"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { PermissionGuard, PermissionButton } from "@/components/Auth"
import { AlertBox } from "@/components"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { hasPermission } from "@/lib/utils"
import {
  useGetBrandApi,
  useUpdateBrandApi,
  useDeleteBrandApi,
  useGetBrandOAuthProvidersApi,
  useCreateBrandOAuthProviderApi,
  useUpdateBrandOAuthProviderApi,
  useDeleteBrandOAuthProviderApi,
  useGetWorkspacesApi,
  brandResponseToBrand,
  oauthProviderResponseToOAuthProvider,
  oauthProvidersResponseToOAuthProviders,
  workspacesResponseToWorkspaces,
  FetchStatus,
} from "@/hooks/api"

import {
  BrandDialog,
  BrandLogoUpload,
  OAuthProviderTable,
  OAuthProviderDialog,
  WorkspaceUsageList,
} from "./components"

const BrandDetail = () => {
  const { t } = useTranslation()
  const { brandId } = useParams<{ brandId: string }>()
  const navigate = useNavigate()
  const { alertToast, successToast } = useToast()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  const [brand, setBrand] = useState<Brand | undefined>()
  const [oauthProviders, setOauthProviders] = useState<BrandOAuthProvider[]>([])
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>([])

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isOAuthDialogOpen, setIsOAuthDialogOpen] = useState(false)
  const [isDeleteOAuthDialogOpen, setIsDeleteOAuthDialogOpen] = useState(false)

  const currentOAuthProvider = useRef<BrandOAuthProvider | undefined>(undefined)

  // APIs
  const {
    data: brandRespData,
    error: brandRespError,
    fetchFn: getBrand,
  } = useGetBrandApi(currentOrganization?.id || "", brandId || "")

  const {
    data: updateBrandRespData,
    error: updateBrandRespError,
    status: updateBrandRespStatus,
    fetchFn: updateBrand,
  } = useUpdateBrandApi(currentOrganization?.id || "", brandId || "")

  const {
    data: deleteBrandRespData,
    error: deleteBrandRespError,
    status: deleteBrandRespStatus,
    fetchFn: deleteBrand,
  } = useDeleteBrandApi(currentOrganization?.id || "", brandId || "")

  const {
    data: oauthProvidersRespData,
    error: oauthProvidersRespError,
    fetchFn: getOAuthProviders,
  } = useGetBrandOAuthProvidersApi(currentOrganization?.id || "", brandId || "")

  const {
    data: createOAuthRespData,
    error: createOAuthRespError,
    status: createOAuthRespStatus,
    fetchFn: createOAuthProvider,
  } = useCreateBrandOAuthProviderApi(
    currentOrganization?.id || "",
    brandId || ""
  )

  const {
    data: updateOAuthRespData,
    error: updateOAuthRespError,
    status: updateOAuthRespStatus,
    fetchFn: updateOAuthProvider,
  } = useUpdateBrandOAuthProviderApi(
    currentOrganization?.id || "",
    brandId || "",
    currentOAuthProvider.current?.id || ""
  )

  const {
    data: deleteOAuthRespData,
    error: deleteOAuthRespError,
    status: deleteOAuthRespStatus,
    fetchFn: deleteOAuthProvider,
  } = useDeleteBrandOAuthProviderApi(
    currentOrganization?.id || "",
    brandId || "",
    currentOAuthProvider.current?.id || ""
  )

  const { data: workspacesData, fetchFn: getWorkspaces } = useGetWorkspacesApi(
    currentOrganization?.id || ""
  )

  // Load brand, oauth providers, and workspaces on mount
  useEffect(() => {
    if (currentOrganization?.id && brandId) {
      getBrand()
      getOAuthProviders({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
      getWorkspaces()
    }
  }, [
    currentOrganization?.id,
    brandId,
    getBrand,
    getOAuthProviders,
    getWorkspaces,
  ])

  // Handle brand data
  useEffect(() => {
    if (!brandRespData || !brandRespData?.data) return
    setBrand(brandResponseToBrand(brandRespData.data))
  }, [brandRespData])

  // Handle brand error
  useEffect(() => {
    if (!brandRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        brandRespError,
        t,
        brandRespData?.message || t("brands.messages.error.loadBrand")
      ),
    })
    navigate("/brands")
  }, [brandRespError, brandRespData, alertToast, t, navigate])

  // Handle OAuth providers data
  useEffect(() => {
    if (!oauthProvidersRespData || !oauthProvidersRespData?.data) return
    setOauthProviders(
      oauthProvidersResponseToOAuthProviders(oauthProvidersRespData.data)
    )
  }, [oauthProvidersRespData])

  // Handle OAuth providers error
  useEffect(() => {
    if (!oauthProvidersRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        oauthProvidersRespError,
        t,
        oauthProvidersRespData?.message ||
          t("brands.messages.error.loadOAuthProviders")
      ),
    })
  }, [oauthProvidersRespError, oauthProvidersRespData, alertToast, t])

  // Handle workspaces data - filter by brand
  useEffect(() => {
    if (workspacesData?.data && brandId) {
      const allWs = workspacesResponseToWorkspaces(workspacesData)
      setAllWorkspaces(allWs.filter((ws) => ws.brand?.id === brandId))
    }
  }, [workspacesData, brandId])

  // Handle update brand success
  useEffect(() => {
    if (!updateBrandRespData || !updateBrandRespData?.data) return

    const updatedBrand = brandResponseToBrand(updateBrandRespData.data)
    setBrand(updatedBrand)
    successToast({
      title: t("brands.messages.updated.title"),
      description: t("brands.messages.updated.description"),
    })
  }, [updateBrandRespData, successToast, t])

  // Handle update brand error
  useEffect(() => {
    if (!updateBrandRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        updateBrandRespError,
        t,
        updateBrandRespData?.message || t("brands.messages.error.updateBrand")
      ),
    })
  }, [updateBrandRespError, updateBrandRespData, alertToast, t])

  // Handle delete brand success
  useEffect(() => {
    if (!deleteBrandRespData) return

    successToast({
      title: t("brands.messages.deleted.title"),
      description: t("brands.messages.deleted.description"),
    })
    navigate("/brands")
  }, [deleteBrandRespData, successToast, t, navigate])

  // Handle delete brand error
  useEffect(() => {
    if (!deleteBrandRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        deleteBrandRespError,
        t,
        deleteBrandRespData?.message || t("brands.messages.error.deleteBrand")
      ),
    })
  }, [deleteBrandRespError, deleteBrandRespData, alertToast, t])

  // Handle create OAuth provider success
  useEffect(() => {
    if (!createOAuthRespData || !createOAuthRespData?.data) return

    const newProvider = oauthProviderResponseToOAuthProvider(
      createOAuthRespData.data
    )
    setOauthProviders((prevProviders) => [...prevProviders, newProvider])
    setIsOAuthDialogOpen(false)

    successToast({
      title: t("brands.messages.oauthCreated.title"),
      description: t("brands.messages.oauthCreated.description"),
    })
  }, [createOAuthRespData, successToast, t])

  // Handle create OAuth provider error
  useEffect(() => {
    if (!createOAuthRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        createOAuthRespError,
        t,
        createOAuthRespData?.message ||
          t("brands.messages.error.createOAuthProvider")
      ),
    })
  }, [createOAuthRespError, createOAuthRespData, alertToast, t])

  // Handle update OAuth provider success
  useEffect(() => {
    if (!updateOAuthRespData || !updateOAuthRespData?.data) return

    const updatedProvider = oauthProviderResponseToOAuthProvider(
      updateOAuthRespData.data
    )
    setOauthProviders((prevProviders) =>
      prevProviders.map((p) =>
        p.id === updatedProvider.id ? updatedProvider : p
      )
    )
    successToast({
      title: t("brands.messages.oauthUpdated.title"),
      description: t("brands.messages.oauthUpdated.description"),
    })
  }, [updateOAuthRespData, successToast, t])

  // Handle update OAuth provider error
  useEffect(() => {
    if (!updateOAuthRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        updateOAuthRespError,
        t,
        updateOAuthRespData?.message ||
          t("brands.messages.error.updateOAuthProvider")
      ),
    })
  }, [updateOAuthRespError, updateOAuthRespData, alertToast, t])

  // Handle delete OAuth provider success
  useEffect(() => {
    if (!deleteOAuthRespData || !deleteOAuthRespData?.data) return

    successToast({
      title: t("brands.messages.oauthDeleted.title"),
      description: t("brands.messages.oauthDeleted.description"),
    })
  }, [deleteOAuthRespData, successToast, t])

  // Handle delete OAuth provider error
  useEffect(() => {
    if (!deleteOAuthRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        deleteOAuthRespError,
        t,
        deleteOAuthRespData?.message ||
          t("brands.messages.error.deleteOAuthProvider")
      ),
    })
  }, [deleteOAuthRespError, deleteOAuthRespData, alertToast, t])

  const handleUpdateBrand = (data: BrandEditData) => {
    updateBrand({
      jsonBody: {
        name: data.name,
        description: data.description,
      },
    })
    setIsEditDialogOpen(false)
  }

  const handleDeleteBrand = () => {
    deleteBrand()
  }

  const handleLogoChange = useCallback((updatedBrand: Brand) => {
    setBrand(updatedBrand)
  }, [])

  const handleCreateOAuthProvider = (data: OAuthProviderEditData) => {
    createOAuthProvider({
      jsonBody: {
        name: data.name,
        provider: data.provider,
        client_id: data.clientId,
        client_secret: data.clientSecret,
        scopes: data.scopes,
      },
    })
  }

  const handleUpdateOAuthProvider = (data: OAuthProviderEditData) => {
    if (!currentOAuthProvider.current) return

    const requestBody: {
      name: string
      client_id: string
      scopes?: string[]
      client_secret?: string
    } = {
      name: data.name,
      client_id: data.clientId,
    }

    // Only include client_secret if provided
    if (data.clientSecret && data.clientSecret.trim() !== "") {
      requestBody.client_secret = data.clientSecret.trim()
    }

    // Compare scopes content deeply, regardless of order
    const currScopes = currentOAuthProvider.current?.scopes ?? []
    const newScopes = data.scopes ?? []
    const areScopesSame =
      currScopes.length === newScopes.length &&
      currScopes.every((scope) => newScopes.includes(scope)) &&
      newScopes.every((scope) => currScopes.includes(scope))

    if (!areScopesSame) {
      requestBody.scopes = data.scopes
    }

    updateOAuthProvider({
      jsonBody: requestBody,
    })
    setIsOAuthDialogOpen(false)
  }

  const handleEditOAuthProvider = (provider: BrandOAuthProvider) => {
    currentOAuthProvider.current = provider
    setIsOAuthDialogOpen(true)
  }

  const handleDeleteOAuthProvider = useCallback(() => {
    if (!currentOAuthProvider.current) return

    deleteOAuthProvider().then(() => {
      getOAuthProviders({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
    })
    setIsDeleteOAuthDialogOpen(false)
  }, [deleteOAuthProvider, getOAuthProviders])

  const isUpdating = updateBrandRespStatus === FetchStatus.Loading
  const isDeleting = deleteBrandRespStatus === FetchStatus.Loading
  const isCreatingOAuth = createOAuthRespStatus === FetchStatus.Loading
  const isUpdatingOAuth = updateOAuthRespStatus === FetchStatus.Loading
  const isDeletingOAuth = deleteOAuthRespStatus === FetchStatus.Loading

  if (!brand) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 mt-4 px-2 pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/brands")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold tracking-tight">{brand.name}</h2>
        </div>
      </div>

      {/* Brand Information Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t("brands.detail.brandInfo")}</CardTitle>
          <div className="flex gap-2">
            <PermissionButton
              permission={Permission.OrgUpdateConnection}
              disabledTooltip={t("common.noPermission")}
              variant="outline"
              size="sm"
              onClick={() => setIsEditDialogOpen(true)}
            >
              <Edit className="mr-1 h-4 w-4" />
              {t("brands.detail.editBrand")}
            </PermissionButton>
            <PermissionButton
              permission={Permission.OrgUpdateConnection}
              disabledTooltip={t("common.noPermission")}
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <span className="text-red-500 flex items-center gap-2">
                <Trash2 className="mr-1 h-4 w-4" />
                {t("brands.detail.deleteBrand")}
              </span>
            </PermissionButton>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-6">
            <div>
              <div className="mt-2">
                <PermissionGuard
                  permission={Permission.OrgUpdateConnection}
                  disabled
                >
                  <BrandLogoUpload
                    brand={brand}
                    onLogoChange={handleLogoChange}
                  />
                </PermissionGuard>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {t("brands.detail.name")}
                </label>
                <p className="mt-1 text-sm">{brand.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium">
                  {t("brands.detail.description")}
                </label>
                <p className="mt-1 text-sm text-muted-foreground">
                  {brand.description}
                </p>
              </div>
            </div>
          </div>
          <Separator />
          <div className="mt-4 flex gap-6 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">
                {t("brands.detail.createdAt")}:
              </span>{" "}
              {format(brand.createdAt, "yyyy-MM-dd")}
            </div>
            <div>
              <span className="font-medium">
                {t("brands.detail.updatedAt")}:
              </span>{" "}
              {format(brand.updatedAt, "yyyy-MM-dd HH:mm:ss")}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OAuth Providers Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t("brands.oauth.title")}</CardTitle>
          <PermissionButton
            permission={Permission.OrgUpdateConnection}
            disabledTooltip={t("common.noPermission")}
            size="sm"
            onClick={() => {
              currentOAuthProvider.current = undefined
              setIsOAuthDialogOpen(true)
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t("brands.oauth.add")}
          </PermissionButton>
        </CardHeader>
        <CardContent>
          <OAuthProviderTable
            providers={oauthProviders}
            onEdit={handleEditOAuthProvider}
            onDelete={(provider) => {
              currentOAuthProvider.current = provider
              setIsDeleteOAuthDialogOpen(true)
            }}
            canEdit={hasPermission(
              currentOrganization?.myPermissions || new Set(),
              Permission.OrgUpdateConnection
            )}
          />
        </CardContent>
      </Card>

      {/* Workspace Usage Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t("brands.workspaces.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <WorkspaceUsageList workspaces={allWorkspaces} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <BrandDialog
        open={isEditDialogOpen}
        initialData={brand}
        isLoading={isUpdating}
        onOpenChange={setIsEditDialogOpen}
        onSubmit={handleUpdateBrand}
      />

      <AlertBox
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={t("brands.delete.title")}
        description={
          <>
            <span>{t("brands.delete.message")}</span>
            {allWorkspaces.length > 0 && (
              <span className="text-destructive font-medium">
                {t("brands.delete.warningWithWorkspaces", {
                  count: allWorkspaces.length,
                })}
              </span>
            )}
          </>
        }
        cancelText={t("brands.delete.cancel")}
        confirmText={
          isDeleting ? t("common.loading") : t("brands.delete.confirm")
        }
        onConfirm={handleDeleteBrand}
      />

      <OAuthProviderDialog
        open={isOAuthDialogOpen}
        initialData={currentOAuthProvider.current}
        isLoading={
          currentOAuthProvider.current ? isUpdatingOAuth : isCreatingOAuth
        }
        onOpenChange={(open) => {
          setIsOAuthDialogOpen(open)
          if (!open) currentOAuthProvider.current = undefined
        }}
        onSubmit={(data) => {
          if (currentOAuthProvider.current) {
            handleUpdateOAuthProvider(data)
          } else {
            handleCreateOAuthProvider(data)
          }
        }}
      />

      <AlertBox
        open={isDeleteOAuthDialogOpen}
        onOpenChange={setIsDeleteOAuthDialogOpen}
        title={t("brands.oauthDelete.title")}
        description={t("brands.oauthDelete.message")}
        cancelText={t("brands.oauthDelete.cancel")}
        confirmText={
          isDeletingOAuth
            ? t("common.loading")
            : t("brands.oauthDelete.confirm")
        }
        onConfirm={handleDeleteOAuthProvider}
      />
    </div>
  )
}

export default BrandDetail
